import { DependencyList, useEffect } from 'react';

import { isUndefined } from 'lodash';
import { useDispatch } from 'react-redux';
import { doGetAccountApplication, getInvestigations } from 'src/actions';
import { AccountStatus, AccountStatusValue } from 'src/models';

import {
  useAccountApplicationSelector,
  UseAccountApplicationSelectorReturnType,
} from './useAccountApplicationSelector';
import { useAccountSelector } from './useAccountSelector';
import { useInvestigationListSelector, UseInvestigationListSelectorReturnType } from './useInvestigationListSelector';

const LINK_ACCOUNT_MAXIMUM_ATTEMPTS = 3;

interface UseFindAccountStatusReturn {
  status?: AccountStatus;
  isAnyDocumentRequested?: boolean;
  investigationList: UseInvestigationListSelectorReturnType;
  application: UseAccountApplicationSelectorReturnType;
}

interface UseFindAccountStatusProps {
  skip?: boolean;
  dependency?: DependencyList;
}

export const useFindAccountStatus = ({
  skip = false,
  dependency = [],
}: UseFindAccountStatusProps): UseFindAccountStatusReturn => {
  const { account } = useAccountSelector();
  const investigationList = useInvestigationListSelector();
  const application = useAccountApplicationSelector();

  const dispatch = useDispatch();

  const findStatus = () => {
    if (!account?.accountId) {
      return;
    }

    if (account?.type.isEntity && !account.isEntityAccountTypeActive) {
      return new AccountStatus(AccountStatusValue.PendingEntityActivation);
    }

    if (account?.accountId && account?.lastFailedLinkAttemptError) {
      return new AccountStatus(AccountStatusValue.PendingLinkError);
    }

    if (investigationList.status?.value) {
      return new AccountStatus(investigationList.status.value);
    }
  };

  const findIsAnyDocumentRequested = () => {
    if (!account?.accountId) {
      return false;
    }

    if (account?.type.isEntity) {
      return application.isAnyAccountApplicationDocumentRequested;
    }

    if (
      account?.lastFailedLinkAttemptError &&
      !isUndefined(account.failedLinkAttempts) &&
      account.failedLinkAttempts < LINK_ACCOUNT_MAXIMUM_ATTEMPTS
    ) {
      return true;
    }

    return investigationList.isAnyDocumentRequested;
  };

  useEffect(() => {
    if (skip) {
      return;
    }

    if (account?.type.isEntity && !account?.isEntityAccountTypeActive) {
      dispatch(doGetAccountApplication({ params: { id: account?.applicationId, accountId: account?.id } }));

      return;
    }

    if (account?.accountId && !account?.lastFailedLinkAttemptError) {
      dispatch(getInvestigations(account?.accountId));
    }
  }, dependency);

  return {
    status: findStatus(),
    isAnyDocumentRequested: findIsAnyDocumentRequested(),
    investigationList,
    application,
  };
};
